import { useState } from 'react';
import { Col, Row, ToggleButton } from 'react-bootstrap';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import EcomEngineAPI from 'api/ecom_engine_api';
import { phoneNumber, phoneNumberLink, trackError } from 'utils/helpers';
import { FORMAT_QT_DATE } from 'utils/helpers/datetime';
import ErrorDialog from 'components/error_dialog';
import LoadingButton from 'components/loading_button/loading_button';

const ReservationErrorDiaglog = ({ show, setShow }) => (
  <ErrorDialog
    id="gift-voucher-error-dialog"
    title="Error"
    handleClose={() => setShow(false)}
    show={show}
  >
    <div className="text-center">
      Something has gone wrong. Please try again. If the problem persists,
      please call us on{' '}
      <a href={`tel:${phoneNumberLink}`} className="text-reset text-nowrap">
        {phoneNumber}
      </a>
    </div>
  </ErrorDialog>
);

const GiftVouchers = ({
  title,
  description,
  purchaseText = 'Purchase gift voucher',
  vouchers,
}) => {
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [reserving, setReserving] = useState(false);
  const [showFailureDialog, setShowFailureDialog] = useState(false);

  const handleError = (error) => {
    trackError(error);
    setShowFailureDialog(true);
    setReserving(false);
  };

  const purchase = () => {
    setReserving(true);

    let params = {
      product: {
        resource_id: selectedVoucher,
        first_travel_date: format(new Date(), FORMAT_QT_DATE),
        passenger_types: { 1: 1 }, // hardcoded one adult, the API requires this for gift vouchers but it's not used
      },
    };

    EcomEngineAPI.resource
      .getResourceByID(selectedVoucher)
      .then((res) => {
        const { type } = res;
        let action;

        if (type === 'Package') {
          action = EcomEngineAPI.reservation.packagesCreate;
        } else {
          action = EcomEngineAPI.reservation.create;
          params = params.product;
        }

        action(params)
          .then(() => {
            // `api_prefix` (derrived from baseSiteUrl) is used to ensure we stay on the same tenant
            window.location.href = `${window.ecom.craftConfig.api_prefix}/bookings`;
          })
          .catch(handleError);
      })
      .catch(handleError);
  };

  return (
    <div className="p-4">
      <h3 className="mb-4">{title}</h3>
      <div className="mb-4">{parse(description || '')}</div>

      <Row xs={2} lg={3} className="g-3">
        {vouchers.map((voucher) => (
          <Col key={voucher.qtResourceId}>
            <ToggleButton
              type="radio"
              name="voucher"
              id={voucher.qtResourceId}
              className="w-100"
              variant="outline-primary"
              checked={selectedVoucher === voucher.qtResourceId}
              disabled={reserving}
              onChange={() => setSelectedVoucher(voucher.qtResourceId)}
            >
              {voucher.giftVoucherTitle}
            </ToggleButton>
          </Col>
        ))}
      </Row>

      <LoadingButton
        className="w-100 mt-6 p-3 btn btn-primary"
        disabled={selectedVoucher === null || reserving}
        onClick={purchase}
        aria-label="Purchase"
        data-ga-events="add_gift_voucher_to_booking"
        loading={reserving}
      >
        {reserving ? 'Submitting...' : purchaseText}
      </LoadingButton>

      <ReservationErrorDiaglog
        show={showFailureDialog}
        setShow={setShowFailureDialog}
      />
    </div>
  );
};

ReservationErrorDiaglog.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
};

GiftVouchers.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  purchaseText: PropTypes.string,
  vouchers: PropTypes.arrayOf(
    PropTypes.shape({
      qtResourceId: PropTypes.number.isRequired,
      giftVoucherTitle: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default GiftVouchers;
